import React from 'react';
import { Box, Loader2 } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { GeneratorGalleryCard } from './GeneratorGalleryCard';
import type { ScreenComponentDef } from '@core/types/slot-screen-component.types';

interface GeneratorGalleryProps {
  component: ScreenComponentDef;
}

export function GeneratorGallery({ component }: GeneratorGalleryProps) {
  const activeScreenId = useSlotScreenGeneratorStore(s => s.activeScreenId);
  const openGeneratorFullView = useSlotScreenGeneratorStore(s => s.openGeneratorFullView);
  const selectComponentResult = useSlotScreenGeneratorStore(s => s.selectComponentResult);
  const selectComponentVideoResult = useSlotScreenGeneratorStore(s => s.selectComponentVideoResult);
  const deleteComponentResult = useSlotScreenGeneratorStore(s => s.deleteComponentResult);

  const readyResults = component.generatedResults.filter(r => r.status === 'ready');
  const generatingResults = component.generatedResults.filter(r => r.status === 'generating');
  const isGenerating = component.status === 'generating';

  // Placeholder count: show at least generatingResults count, or imageCount if generating
  const placeholderCount = isGenerating
    ? Math.max(0, (component.generationImageCount ?? 4) - generatingResults.length - readyResults.length)
    : generatingResults.length;

  const hasContent = readyResults.length > 0 || placeholderCount > 0;

  if (!hasContent) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[300px]">
        <div className="text-center text-zinc-500">
          <Box size={48} className="mx-auto mb-3 text-zinc-600" />
          <p className="text-sm font-medium">{component.name}</p>
          <p className="text-xs mt-1">{component.width} x {component.height} px</p>
          <p className="text-xs mt-3">Write a prompt below to generate this component</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <style>{`
        .generator-gallery-masonry { column-count: 2; column-gap: 12px; }
        @media (min-width: 600px) {
          .generator-gallery-masonry { column-count: 3; }
        }
        @media (min-width: 900px) {
          .generator-gallery-masonry { column-count: 4; }
        }
      `}</style>

      <div className="generator-gallery-masonry">
        {readyResults.map((result, index) => (
          <GeneratorGalleryCard
            key={result.id}
            result={result}
            isSelected={result.id === component.selectedResultId}
            onClick={() => openGeneratorFullView(index)}
            onSelect={() => result.type === 'video'
              ? selectComponentVideoResult(activeScreenId, component.id, result.id)
              : selectComponentResult(activeScreenId, component.id, result.id)
            }
            onDelete={() => deleteComponentResult(activeScreenId, component.id, result.id)}
          />
        ))}

        {/* Generating placeholders */}
        {Array.from({ length: placeholderCount }).map((_, i) => (
          <div
            key={`placeholder-${i}`}
            className="break-inside-avoid mb-3 rounded-lg overflow-hidden border border-dashed border-zinc-700 bg-zinc-900/50"
            style={{ aspectRatio: `${component.width} / ${component.height}` }}
          >
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <Loader2 size={20} className="text-zinc-500 animate-spin" />
              <span className="text-[10px] text-zinc-500">Generating...</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
